import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchVideoUrl, fetchMovieDetail, fetchEpisodeDetails, fetchSeriesDetail, getBookmark, updateWatchProgress, resolveProductIdForPackages } from "../../redux/content";
import { fetchPurchaseHistory } from "../../redux/subscription";
import ReportShowModal from "../../components/modals/ReportShowModal";
import useHls from "../../utils/useHls";
import "../../components/styles/videoPlayerPage.scss";

/**
 * VideoPlayerPage — Full-screen video player
 * Figma: Video Player (Desktop) (24654:5295) — 1920×480
 *
 * Now wired to real Client API:
 *   - Video URL via fetchVideoUrl() (movie, episode, or live channel)
 *   - Movie/episode details for title display
 *   - URL params: /player/:id?type=movie|series|live
 *   - Report a Show modal (Figma: 24654:6528)
 */

// Fallback options when HLS track data isn't available
const FALLBACK_RESOLUTION = [{ id: -1, label: "Auto" }];
const FALLBACK_SUBTITLE = [{ id: -1, label: "Off" }];
const FALLBACK_AUDIO = [{ id: 0, label: "Default" }];

const VideoPlayerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // UID for movies/live, numeric ID for episodes
  const [searchParams] = useSearchParams();
  const contentType = searchParams.get("type") || "movie";

  // Navigation state from details/home page (carries numeric ID for movie details API)
  const navState = location.state || {};
  const numericMovieId = navState.movieId; // numeric ID for /movies/{id} endpoint
  const navIsPurchased = navState.isPurchased ?? null; // per-content purchase status from MovieDetailsPage

  const playerRef = useRef(null); // whole player container for fullscreen
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showEpisodeInfo, setShowEpisodeInfo] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [currentTime, setCurrentTime] = useState("00:00");
  const [totalTime, setTotalTime] = useState("00:00");
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [resumeTime, setResumeTime] = useState(null); // bookmark resume position (seconds)
  const [episodeDetail, setEpisodeDetail] = useState(null); // episode metadata when contentType is "series"
  const [episodeQueue, setEpisodeQueue] = useState([]); // ordered episodes across all seasons (for next/prev)
  const [pickPlanProductId, setPickPlanProductId] = useState(null); // resolved TV Store product id for Subscribe Now CTA
  const controlsTimer = useRef(null);
  const bookmarkTimer = useRef(null); // periodic bookmark save
  const lastSavedTime = useRef(0); // track last saved position

  // Redux state
  const { videoUrl, movieDetails, loaders, errors } = useSelector((state) => state.content);
  const { premiumSub } = useSelector((state) => state.subscription);
  // activeProductIds is the CRM source of truth for per-product entitlement —
  // movie.purchased and TV Store price_options.purchased are unreliable (wiki:
  // backend returns true for every TVOD item regardless of actual rental state).
  const activeProductIds = useSelector((state) => state.subscription.activeProductIds);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  // Local movie detail snapshot (for SVOD fallback check on movie.purchased).
  const [movieDetailSnapshot, setMovieDetailSnapshot] = useState(null);

  // Per-content entitlement — CRM activeProductIds is the source of truth.
  //   - If nav state passed an explicit isPurchased flag (from MovieDetailsPage
  //     which has already done the CRM check), trust it.
  //   - If we've resolved a TV Store productId, require it in activeProductIds.
  //   - Only fall back to movie.purchased for pure SVOD where no purchasable
  //     product exists (e.g. bundled freetowatch tier) — that field is reliable
  //     for bouquet membership per the MovieDetailsPage comment.
  //   - Live content uses blanket premiumSub (not tied to a specific product).
  const contentPurchased = (() => {
    if (contentType === "live") return premiumSub;
    if (navIsPurchased !== null) return navIsPurchased;
    if (pickPlanProductId) {
      return Array.isArray(activeProductIds)
        && activeProductIds.some((pid) => String(pid) === String(pickPlanProductId));
    }
    // No purchasable product owns this content's packages — defer to SVOD flag,
    // but only once the movie detail has actually loaded so we don't greenlight
    // playback on the default `null` / `undefined`.
    if (movieDetailSnapshot) return !!movieDetailSnapshot.purchased;
    return null; // still resolving
  })();

  // HLS playback — handles .m3u8 on all browsers + quality/audio/subtitle tracks
  const playbackUrl = videoUrl?.url || videoUrl?.stream_url || null;
  const {
    qualities, currentQuality, setQuality,
    audioTracks: hlsAudioTracks, currentAudioTrack, setAudioTrack,
    subtitleTracks: hlsSubtitleTracks, currentSubtitleTrack, setSubtitleTrack,
  } = useHls(videoRef, playbackUrl, { autoPlay: true });

  // Build options for settings panel from HLS tracks (or fallback)
  const resolutionOptions = qualities.length > 0
    ? [{ id: -1, label: "Auto" }, ...qualities.map((q) => ({ id: q.index, label: q.label }))]
    : FALLBACK_RESOLUTION;
  const subtitleOptions = hlsSubtitleTracks.length > 0
    ? [{ id: -1, label: "Off" }, ...hlsSubtitleTracks.map((t) => ({ id: t.index, label: t.name || t.lang }))]
    : FALLBACK_SUBTITLE;
  const audioOptions = hlsAudioTracks.length > 0
    ? hlsAudioTracks.map((t) => ({ id: t.index, label: t.name || t.lang || `Audio ${t.index + 1}` }))
    : FALLBACK_AUDIO;

  // Safe string extraction
  const safeStr = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") return val.uid || val.name || val.value || "";
    return String(val);
  };

  // Refresh CRM purchase history + movie detail snapshot on mount.
  // Entitlement is derived downstream from activeProductIds, not from this fetch.
  useEffect(() => {
    const initPlayer = async () => {
      try {
        await fetchPurchaseHistory("Active");
        if (contentType === "movie") {
          const detailId = numericMovieId || id;
          const movie = await fetchMovieDetail(detailId);
          if (movie) {
            setMovieDetailSnapshot(movie);
            console.log("[VideoPlayerPage] Movie detail loaded:", movie.title, "purchased_flag:", movie.purchased);
          }
        }
        setSubscriptionChecked(true);
      } catch (error) {
        console.error("[VideoPlayerPage] Subscription check failed:", error);
        setSubscriptionChecked(true);
      }
    };
    initPlayer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Once subscription is verified, fetch video URL only if THIS content is purchased
  useEffect(() => {
    if (!subscriptionChecked || !id) return;

    // For live channels, use blanket premiumSub (not tied to a specific product)
    const canPlay = contentType === "live" ? premiumSub : contentPurchased;

    if (canPlay) {
      console.log("[VideoPlayerPage] Content access granted, fetching video:", id, contentType);
      fetchVideoUrl(id, contentType);
    } else {
      console.log("[VideoPlayerPage] Content NOT purchased:", id, contentType, "premiumSub:", premiumSub, "contentPurchased:", contentPurchased);
    }

    // Fetch detail for title/info overlay. Movies and episodes use different endpoints:
    //   movie:   GET /users/{uid}/movies/{id}
    //   episode: GET /episodes?episodes_id={id}  (returns a map keyed by id)
    if (contentType === "movie") {
      const detailId = numericMovieId || id;
      const alreadyLoaded = movieDetails && numericMovieId && movieDetails.id === numericMovieId;
      if (!alreadyLoaded) {
        fetchMovieDetail(detailId);
      }
    } else if (contentType === "series") {
      const episodeId = numericMovieId || id;
      const alreadyLoaded = episodeDetail && String(episodeDetail.id) === String(episodeId);
      if (!alreadyLoaded) {
        fetchEpisodeDetails([episodeId]).then((map) => {
          const ep = map?.[episodeId] || Object.values(map || {})[0] || null;
          if (ep) {
            console.log("[VideoPlayerPage] Episode loaded:", ep.id, ep.title);
            setEpisodeDetail(ep);
          }
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, contentType, subscriptionChecked, contentPurchased, premiumSub, numericMovieId]);

  // Resolve the TV Store product that owns this content's packages. Needed both
  // for the per-product entitlement check below and for the Subscribe Now CTA.
  useEffect(() => {
    if (contentType === "live") return;
    const resolve = async () => {
      let packages = [];
      if (contentType === "movie") {
        // fetchMovieDetail returns packages as [{uid, id, ...}]; resolver accepts
        // both object and bare-id forms.
        const detail = movieDetailSnapshot
          || (movieDetails && String(movieDetails.id) === String(numericMovieId || id) ? movieDetails : null)
          || (await fetchMovieDetail(numericMovieId || id));
        packages = detail?.packages || [];
      } else if (contentType === "series") {
        // Episodes don't carry packages — packages live on the parent series.
        const seriesId = episodeDetail?.series_id;
        if (!seriesId) return;
        const series = await fetchSeriesDetail(seriesId);
        packages = series?.packages || [];
      }

      if (packages.length === 0) return;
      const productId = await resolveProductIdForPackages(packages);
      console.log("[VideoPlayerPage] Resolved productId:", productId, "for", contentType, "packages:", packages);
      if (productId) setPickPlanProductId(productId);
    };
    resolve();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, movieDetails, movieDetailSnapshot, episodeDetail, numericMovieId, id]);

  // Build the ordered episode queue for the parent series so we can offer
  // Next/Previous navigation and auto-play the following episode on end.
  // Episodes are flattened across all seasons in season → episode order, so
  // "next" at the end of a season rolls into the first episode of the next.
  useEffect(() => {
    if (contentType !== "series" || !episodeDetail?.series_id) return;
    let cancelled = false;
    fetchSeriesDetail(episodeDetail.series_id).then((series) => {
      if (cancelled || !series?.seasons) return;
      const flat = series.seasons.flatMap((s) => s.episodes || []);
      console.log("[VideoPlayerPage] Episode queue built:", flat.length, "episodes for series", episodeDetail.series_id);
      setEpisodeQueue(flat);
    });
    return () => { cancelled = true; };
  }, [contentType, episodeDetail?.series_id]);

  // 3. Fetch bookmark (resume position) when we have the numeric ID
  useEffect(() => {
    if (!numericMovieId || !contentPurchased) return;
    // Clear any stale resume position carried over from a previous episode.
    setResumeTime(null);
    const fetchResume = async () => {
      const timeMs = await getBookmark(numericMovieId, contentType);
      if (timeMs && timeMs > 0) {
        const timeSec = timeMs / 1000;
        setResumeTime(timeSec);
      }
    };
    fetchResume();
  }, [numericMovieId, contentType, contentPurchased]);

  // 4. Apply resume position when video is ready
  useEffect(() => {
    const video = videoRef.current;
    if (video && resumeTime && resumeTime > 0) {
      const handleCanPlay = () => {
        if (video.currentTime < 1) {
          video.currentTime = resumeTime;
        }
        video.removeEventListener("canplay", handleCanPlay);
      };
      video.addEventListener("canplay", handleCanPlay);
      return () => video.removeEventListener("canplay", handleCanPlay);
    }
  }, [resumeTime, videoUrl]);

  // 5. Save bookmark periodically (every 30s) and on unmount
  useEffect(() => {
    if (!numericMovieId) return;

    bookmarkTimer.current = setInterval(() => {
      const video = videoRef.current;
      if (video && video.currentTime > 0 && !video.paused) {
        const timeMs = Math.floor(video.currentTime * 1000);
        // Only save if position changed significantly (>5s)
        if (Math.abs(timeMs - lastSavedTime.current) > 5000) {
          lastSavedTime.current = timeMs;
          updateWatchProgress(numericMovieId, contentType, timeMs);
        }
      }
    }, 30000);

    const videoEl = videoRef.current;
    return () => {
      // Save final position on unmount
      clearInterval(bookmarkTimer.current);
      if (videoEl && videoEl.currentTime > 0 && numericMovieId) {
        const timeMs = Math.floor(videoEl.currentTime * 1000);
        updateWatchProgress(numericMovieId, contentType, timeMs);
      }
    };
  }, [numericMovieId, contentType]);

  // Format time from seconds to HH:MM:SS or MM:SS
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "00:00";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  // Video event handlers
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setCurrentTime(formatTime(video.currentTime));
      setTotalTime(formatTime(video.duration));
      setProgress((video.currentTime / video.duration) * 100 || 0);
    }
  }, []);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    video.currentTime = pct * video.duration;
  };

  const handleSkip = (seconds) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + seconds));
    }
  };

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  // Fullscreen: target the whole player container (not just <video>)
  // This way controls, overlays, and modals all stay visible in fullscreen
  const handleFullscreen = () => {
    const el = playerRef.current;
    if (!el) return;

    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  // Track fullscreen state changes
  useEffect(() => {
    const handleFSChange = () => {
      setIsFullscreen(
        !!(document.fullscreenElement || document.webkitFullscreenElement)
      );
    };
    document.addEventListener("fullscreenchange", handleFSChange);
    document.addEventListener("webkitfullscreenchange", handleFSChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFSChange);
      document.removeEventListener("webkitfullscreenchange", handleFSChange);
    };
  }, []);

  // Auto-hide controls
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    if (controlsTimer.current) clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => setShowControls(false), 4000);
  }, []);

  useEffect(() => {
    return () => {
      if (controlsTimer.current) clearTimeout(controlsTimer.current);
    };
  }, []);

  // Report handler
  const handleReportSubmit = ({ issue, reason }) => {
    // TODO: POST to reporting API when endpoint is available
  };

  // Resolve current position in the episode queue → enables Next/Previous.
  const currentEpisodeId = numericMovieId || id;
  const episodeIndex = contentType === "series"
    ? episodeQueue.findIndex((e) => String(e.id) === String(currentEpisodeId))
    : -1;
  const prevEpisode = episodeIndex > 0 ? episodeQueue[episodeIndex - 1] : null;
  const nextEpisode = episodeIndex >= 0 && episodeIndex < episodeQueue.length - 1
    ? episodeQueue[episodeIndex + 1]
    : null;
  const hasEpisodeNav = contentType === "series" && episodeQueue.length > 1;

  // Navigate to another episode within the same player. `replace` keeps the
  // browser back button pointing at the series page, not each episode visited.
  const goToEpisode = (ep) => {
    if (!ep) return;
    console.log("[VideoPlayerPage] Navigating to episode:", ep.id, ep.title);
    navigate(`/player/${ep.id}?type=series`, {
      state: {
        movieId: ep.id,
        title: ep.title || `Episode ${ep.number}`,
        seriesTitle: navState.seriesTitle,
      },
      replace: true,
    });
  };

  // Prefer episode detail for series, else movie detail, else nav state
  const displayDetail = contentType === "series" ? episodeDetail : movieDetails;
  const contentTitle = displayDetail
    ? safeStr(displayDetail.title)
    : navState.title
    ? safeStr(navState.title)
    : contentType === "live"
    ? "Live Channel"
    : "Loading...";

  // Determine what message to show when there's no video
  const getStatusMessage = () => {
    // Still checking subscription
    if (!subscriptionChecked) return "Checking subscription...";
    // Loading video from API
    if (loaders.video) return "Loading video...";
    // Content not purchased (per-content check)
    const canPlay = contentType === "live" ? premiumSub : contentPurchased;
    if (!canPlay) return null; // handled by subscribe prompt below
    // API returned an error
    if (errors?.video) return errors.video;
    // Local video element error
    if (videoError) return videoError;
    // API returned ok but no playable URL in the data
    if (videoUrl && !playbackUrl) return "Video stream not available for this content.";
    // API hasn't returned yet or returned null
    if (!playbackUrl) return "Video not available.";
    return null;
  };

  return (
    <div
      ref={playerRef}
      className="video-player"
      onMouseMove={resetControlsTimer}
      onClick={resetControlsTimer}
    >
      {/* Video element */}
      {playbackUrl ? (
        <video
          ref={videoRef}
          className="video-player__screen"
          autoPlay
          onTimeUpdate={handleTimeUpdate}
          onError={() => setVideoError("Failed to load video stream.")}
          onEnded={() => {
            setIsPlaying(false);
            if (nextEpisode) {
              console.log("[VideoPlayerPage] Episode ended → auto-playing next:", nextEpisode.id);
              goToEpisode(nextEpisode);
            }
          }}
        />
      ) : (
        <div className="video-player__screen">
          {/* Content not purchased — show subscribe prompt.
              Gate on EXPLICIT false (not falsy): contentPurchased starts as null
              while the productId resolution chain runs (episode → series → packages
              → product). Showing Subscribe Now on null caused a flash before the
              entitlement resolved. */}
          {(() => {
            const accessFlag = contentType === "live" ? premiumSub : contentPurchased;
            const showUnsubscribed = subscriptionChecked && accessFlag === false;
            return showUnsubscribed ? (
              <div className="video-player__unsubscribed">
                <p className="video-player__unsubscribed-text">
                  You don't have access to this content. Subscribe to watch.
                </p>
                <button
                  className="video-player__subscribe-btn"
                  onClick={() => {
                    if (pickPlanProductId) {
                      console.log("[VideoPlayerPage] Subscribe Now → PickPlanPage", pickPlanProductId);
                      navigate(`/discover/product/${pickPlanProductId}/plan`, {
                        state: {
                          movieId: numericMovieId,
                          movieUid: id,
                          // Carry the content type so the post-payment redirect
                          // sends the user back to the player with the correct
                          // ?type=. Without this the callback defaults to
                          // type=movie and an episode id played as a movie hangs
                          // on "Loading…" forever.
                          returnType: contentType,
                          returnPath: `${location.pathname}${location.search}`,
                        },
                      });
                    } else {
                      console.warn("[VideoPlayerPage] No product resolved, falling back to settings");
                      navigate("/settings/subscription");
                    }
                  }}
                >
                  Subscribe Now
                </button>
              </div>
            ) : (
              <div className="video-player__loading">
                {getStatusMessage()}
              </div>
            );
          })()}
        </div>
      )}

      {/* Top bar */}
      {showControls && (
        <div className="video-player__top-bar">
          <div className="video-player__top-left">
            <button
              className="video-player__back-btn"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="video-player__title">{contentTitle}</span>
          </div>
          <div className="video-player__top-right">
            {/* Report / Warning icon */}
            <button
              className="video-player__icon-btn"
              aria-label="Report"
              onClick={() => setShowReportModal(true)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
                <path d="M12 8V12M12 16H12.01" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <button
              className="video-player__icon-btn"
              aria-label="Info"
              onClick={() => setShowEpisodeInfo(!showEpisodeInfo)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
                <path d="M12 16V12M12 8H12.01" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <button className="video-player__icon-btn" aria-label="Cast">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M2 16.1A5 5 0 015.9 20M2 12.05A9 9 0 019.95 20M2 8V6a2 2 0 012-2h16a2 2 0 012 2v12a2 2 0 01-2 2h-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="2" cy="20" r="1" fill="white" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Report a Show modal */}
      <ReportShowModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleReportSubmit}
      />

      {/* Episode info overlay */}
      {showEpisodeInfo && displayDetail && (
        <div className="video-player__episode-overlay" onClick={() => setShowEpisodeInfo(false)}>
          <div className="video-player__episode-info" onClick={(e) => e.stopPropagation()}>
            <span className="video-player__episode-label">
              {contentType === "series" ? "Episode" : "Movie"}
            </span>
            <h2 className="video-player__episode-title">
              {safeStr(displayDetail.title)}
            </h2>
            <p className="video-player__episode-desc">
              {safeStr(displayDetail.description) || safeStr(displayDetail.short_description)}
            </p>
          </div>
        </div>
      )}

      {/* Settings panel — Resolution/Subtitle/Audio not yet available via API */}
      {showSettingsPanel && (
        <div className="video-player__panel-overlay" onClick={() => setShowSettingsPanel(false)}>
          <div className="video-player__settings-panel" onClick={(e) => e.stopPropagation()}>
            <div className="video-player__settings-col">
              <h3 className="video-player__settings-heading">Resolution</h3>
              {resolutionOptions.map((opt) => (
                <button
                  key={opt.id}
                  className={`video-player__settings-option ${
                    currentQuality === opt.id ? "video-player__settings-option--active" : ""
                  }`}
                  onClick={() => setQuality(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="video-player__settings-col">
              <h3 className="video-player__settings-heading">Subtitle</h3>
              {subtitleOptions.map((opt) => (
                <button
                  key={opt.id}
                  className={`video-player__settings-option ${
                    currentSubtitleTrack === opt.id ? "video-player__settings-option--active" : ""
                  }`}
                  onClick={() => setSubtitleTrack(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="video-player__settings-col">
              <h3 className="video-player__settings-heading">Audio</h3>
              {audioOptions.map((opt) => (
                <button
                  key={opt.id}
                  className={`video-player__settings-option ${
                    currentAudioTrack === opt.id ? "video-player__settings-option--active" : ""
                  }`}
                  onClick={() => setAudioTrack(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              className="video-player__settings-close"
              onClick={() => setShowSettingsPanel(false)}
              aria-label="Close settings"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="white" strokeWidth="1.5" />
                <path d="M8 8L16 16M16 8L8 16" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Bottom controls */}
      {showControls && (
        <div className="video-player__controls">
          <div className="video-player__controls-row">
            <span className="video-player__time">
              {currentTime} / {totalTime}
            </span>

            <div className="video-player__center-controls">
              {hasEpisodeNav && (
                <button
                  className="video-player__control-btn"
                  aria-label="Previous episode"
                  onClick={() => goToEpisode(prevEpisode)}
                  disabled={!prevEpisode}
                  style={{ opacity: prevEpisode ? 1 : 0.4, cursor: prevEpisode ? "pointer" : "default" }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M19 20L9 12L19 4V20Z" fill="white" />
                    <rect x="5" y="4" width="2.5" height="16" rx="1" fill="white" />
                  </svg>
                </button>
              )}

              <button
                className="video-player__control-btn"
                aria-label="Rewind 10 seconds"
                onClick={() => handleSkip(-10)}
              >
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <path d="M14 6C8.48 6 4 10.48 4 16s4.48 10 10 10 10-4.48 10-10" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M14 2V6L10 4" fill="white" />
                  <text x="10" y="19" fill="white" fontSize="8" fontWeight="600">10</text>
                </svg>
              </button>

              <button
                className="video-player__play-pause"
                onClick={handlePlayPause}
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="16" r="15" stroke="white" strokeWidth="1.5" />
                    <rect x="11" y="10" width="3" height="12" rx="1" fill="white" />
                    <rect x="18" y="10" width="3" height="12" rx="1" fill="white" />
                  </svg>
                ) : (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="16" r="15" stroke="white" strokeWidth="1.5" />
                    <path d="M13 10L22 16L13 22V10Z" fill="white" />
                  </svg>
                )}
              </button>

              <button
                className="video-player__control-btn"
                aria-label="Forward 10 seconds"
                onClick={() => handleSkip(10)}
              >
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <path d="M14 6c5.52 0 10 4.48 10 10s-4.48 10-10 10S4 21.52 4 16" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M14 2V6L18 4" fill="white" />
                  <text x="10" y="19" fill="white" fontSize="8" fontWeight="600">10</text>
                </svg>
              </button>

              {hasEpisodeNav && (
                <button
                  className="video-player__control-btn"
                  aria-label="Next episode"
                  onClick={() => goToEpisode(nextEpisode)}
                  disabled={!nextEpisode}
                  style={{ opacity: nextEpisode ? 1 : 0.4, cursor: nextEpisode ? "pointer" : "default" }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M5 4L15 12L5 20V4Z" fill="white" />
                    <rect x="16.5" y="4" width="2.5" height="16" rx="1" fill="white" />
                  </svg>
                </button>
              )}
            </div>

            <div className="video-player__right-controls">
              <button
                className="video-player__control-btn"
                onClick={handleMuteToggle}
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" fill="white" />
                    <path d="M23 9l-6 6M17 9l6 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" fill="white" />
                    <path d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                )}
              </button>

              <button
                className="video-player__control-btn"
                aria-label="Settings"
                onClick={() => setShowSettingsPanel(!showSettingsPanel)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="white" strokeWidth="1.5" />
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="white" strokeWidth="1.5" />
                </svg>
              </button>

              <button
                className="video-player__control-btn"
                aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                onClick={handleFullscreen}
              >
                {isFullscreen ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M4 14h4v4M20 14h-4v4M4 10h4V6M20 10h-4V6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div
            className="video-player__progress-track"
            onClick={handleSeek}
          >
            <div
              className="video-player__progress-fill"
              style={{ width: `${progress}%` }}
            />
            <div
              className="video-player__progress-thumb"
              style={{ left: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayerPage;
